import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { SettingModalPresenter } from "./SettingModal.presenter";
import {
  Setting,
  SettingCreateRequestPayload,
  SettingUpdateRequestPayload,
} from "@/domain/setting.interface";
import { useSettingCreateOne, useSettingUpdateOne } from "@/hooks/useSetting";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import validationSchema, { SettingFormData } from "./validation.schema";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal";
import { ErrorResponse } from "@/domain/error.interface";

export interface SettingModalContainerProps {
  open: boolean;
  mode: "create" | "edit";
  setting?: Setting | null;
  onClose: () => void;
  onSuccess?: () => void;
  canAddSetting: boolean;
  canEditSetting: boolean;
}

export const SettingModalContainer: FC<SettingModalContainerProps> = ({
  open,
  mode,
  setting,
  onClose,
  onSuccess,
  canAddSetting,
  canEditSetting,
}) => {
  const { t } = useTranslation("common");
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const createAuditLog = useCreateAuditlog();

  // Mutations
  const settingCreateOne = useSettingCreateOne();
  const settingUpdateOne = useSettingUpdateOne();

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPermissionDeniedModalOpen, setIsPermissionDeniedModalOpen] =
    useState(false);

  // Form setup
  const methods = useForm<SettingFormData>({
    resolver: yupResolver(validationSchema(t)),
    mode: "all",
    defaultValues: {
      product: "",
      application: undefined,
      module: "",
      section: "",
      name: "",
      description: "",
      type: undefined,
      default: "",
      value: undefined,
      active: true,
    },
  });

  // Initialize form data when setting is loaded (edit mode)
  useEffect(() => {
    if (mode === "edit" && setting && open) {
      methods.reset({
        product: setting.product,
        application: setting.application as any,
        module: setting.module,
        section: setting.section,
        name: setting.name,
        description: setting.description,
        type: setting.type as any,
        default: Array.isArray(setting.default)
          ? JSON.stringify(setting.default)
          : setting.default,
        value:
          setting.accountSettings?.value !== undefined
            ? Array.isArray(setting.accountSettings.value)
              ? JSON.stringify(setting.accountSettings.value)
              : setting.accountSettings.value
            : undefined,
        active: setting.active,
      });
    } else if (mode === "create" && open) {
      methods.reset({
        product: "",
        application: undefined,
        module: "",
        section: "",
        name: "",
        description: "",
        type: undefined,
        default: "",
        value: undefined,
        active: true,
      });
    }
  }, [mode, setting, open, methods]);

  // Handle submit
  const handleSubmit = async () => {
    if (mode === "create" && !canAddSetting) {
      showSnackBar(t("generic_not_allowed") || "Not allowed", "error");
      return;
    }
    if (mode === "edit" && !canEditSetting) {
      showSnackBar(t("generic_not_allowed") || "Not allowed", "error");
      return;
    }

    const isValid = await methods.trigger();
    if (!isValid) {
      return;
    }

    const formDataValues = methods.getValues();

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const payload: SettingCreateRequestPayload = {
          product: formDataValues.product,
          application: formDataValues.application,
          module: formDataValues.module,
          section: formDataValues.section,
          name: formDataValues.name,
          description: formDataValues.description,
          type: formDataValues.type,
          default:
            formDataValues.type === "array" &&
            typeof formDataValues.default === "string"
              ? JSON.parse(formDataValues.default)
              : formDataValues.default,
          value:
            formDataValues.type === "array" &&
            typeof formDataValues.value === "string" &&
            formDataValues.value
              ? JSON.parse(formDataValues.value)
              : formDataValues.value,
          active: formDataValues.active,
          createdBy: userInfo?._id || "",
        } as SettingCreateRequestPayload;

        await settingCreateOne.mutateAsync(payload);
        showSnackBar("Setting created successfully", "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.SETTINGS,
          option: OPTIONS.CREATE,
          actionCode: ACTION_CODE.WEB_SETTINGS_CREATE,
          action: "When creating a new setting",
          detail: `Created setting ${formDataValues.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      } else {
        const payload: SettingUpdateRequestPayload = {
          _id: setting?._id || "",
          product: formDataValues.product,
          application: formDataValues.application,
          module: formDataValues.module,
          section: formDataValues.section,
          name: formDataValues.name,
          description: formDataValues.description,
          type: formDataValues.type,
          default:
            formDataValues.type === "array" &&
            typeof formDataValues.default === "string"
              ? JSON.parse(formDataValues.default)
              : formDataValues.default,
          value:
            formDataValues.type === "array" &&
            typeof formDataValues.value === "string" &&
            formDataValues.value
              ? JSON.parse(formDataValues.value)
              : formDataValues.value,
          active: formDataValues.active,
          updatedBy: userInfo?._id || "",
        } as SettingUpdateRequestPayload;

        await settingUpdateOne.mutateAsync({
          id: setting?._id || "",
          data: payload,
        });
        showSnackBar("Setting updated successfully", "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.SETTINGS,
          option: OPTIONS.EDIT,
          actionCode: ACTION_CODE.WEB_SETTINGS_EDIT,
          action: "When editing a setting",
          detail: `Edited setting ${formDataValues.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const err = error as ErrorResponse;
      const errorMessage = Array.isArray(err.message)
        ? err.message[0]
        : err.message || err.error || "An error occurred";

      showSnackBar(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <SettingModalPresenter
          open={open}
          mode={mode}
          isSubmitting={
            isSubmitting ||
            settingCreateOne.isPending ||
            settingUpdateOne.isPending
          }
          isLoadingSettingData={false}
          onClose={onClose}
          onSubmit={handleSubmit}
        />
      </FormProvider>

      {/* Permission Denied Modal */}
      <PermissionDeniedModal
        open={isPermissionDeniedModalOpen}
        onClose={() => setIsPermissionDeniedModalOpen(false)}
      />
    </>
  );
};
